import React, { useState, useEffect } from 'react'
import { API_BASE_URL } from '../config/api';
import { ArrowLeft, Mail, Save, TestTube, Copy, Lock, ShieldCheck, Send } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import {
  isPasswordPlaceholder,
  messageForSmtpError,
  interpretSmtpTestResponse,
  validateTestRecipientEmail,
  interpretSendTestResponse,
  buildSendTestPayload,
} from '../utils/smtpTestUi'

const EmailConfig = () => {
  const navigate = useNavigate()
  const [config, setConfig] = useState({
    smtp_server: 'smtp.office365.com',
    smtp_port: 587,
    smtp_user: 'proyectos.td@induma.com.co',
    smtp_password: '',
    from_email: '',
    security: 'TLS'
  })
  const [loading, setLoading] = useState(false)
  const [testingEntered, setTestingEntered] = useState(false)
  const [testingStored, setTestingStored] = useState(false)
  const [testRecipientEmail, setTestRecipientEmail] = useState('')
  const [sendingTest, setSendingTest] = useState(false)

  useEffect(() => {
    loadConfig()
  }, [])

  const getAuthHeaders = (extraHeaders = {}) => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token')
    return {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...extraHeaders,
    }
  }

  const loadConfig = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/config/email`, {
        headers: getAuthHeaders(),
      })
      if (response.ok) {
        const data = await response.json()
        if (data) {
          setConfig({
            smtp_server: data.smtp_server || 'smtp.office365.com',
            smtp_port: data.smtp_port || 587,
            smtp_user: data.smtp_user || 'proyectos.td@induma.com.co',
            smtp_password: '',
            from_email: data.from_email || '',
            security: data.security || 'TLS'
          })
        }
      }
    } catch (error) {
      console.error('Error al cargar configuración:', error)
    }
  }

  const handleSave = async () => {
    const fromEmail = (config.from_email || '').trim()
    if (fromEmail && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(fromEmail)) {
      toast.error('El campo "Remitente visible" debe ser un correo válido')
      return
    }

    setLoading(true)
    try {
      const response = await fetch(`${API_BASE_URL}/api/config/email`, {
        method: 'POST',
        headers: getAuthHeaders({
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify({ ...config, from_email: fromEmail || null }),
      })

      if (response.ok) {
        toast.success('Configuración de correo guardada exitosamente')
        setConfig((prev) => ({ ...prev, smtp_password: '' }))
      } else {
        const error = await response.json()
        toast.error(error.detail || 'Error al guardar configuración')
      }
    } catch (error) {
      toast.error('Error al guardar configuración')
    } finally {
      setLoading(false)
    }
  }

  const handleTestEntered = async () => {
    if (isPasswordPlaceholder(config.smtp_password)) {
      toast.error(messageForSmtpError('PASSWORD_REQUIRED'))
      return
    }

    setTestingEntered(true)
    try {
      // Payload sanitizado (sin valor de contraseña) solo para depuración local
      console.log('[EmailConfig] test entered password', {
        smtp_server: config.smtp_server,
        smtp_port: config.smtp_port,
        smtp_user: config.smtp_user,
        from_email: config.from_email,
        security: config.security,
        password_present: Boolean(config.smtp_password),
        password_length: (config.smtp_password || '').length,
        password_is_placeholder: isPasswordPlaceholder(config.smtp_password),
      })

      const response = await fetch(`${API_BASE_URL}/api/config/email/test`, {
        method: 'POST',
        headers: getAuthHeaders({
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify({
          smtp_server: config.smtp_server,
          smtp_port: config.smtp_port,
          smtp_user: config.smtp_user,
          smtp_password: config.smtp_password,
          from_email: config.from_email || null,
          security: config.security,
        }),
      })

      const data = await response.json().catch(() => ({}))
      const interpreted = interpretSmtpTestResponse(response.status, data)
      if (interpreted.ok) {
        toast.success(interpreted.message)
      } else {
        toast.error(interpreted.message)
      }
    } catch (error) {
      toast.error('Error al probar la contraseña ingresada')
    } finally {
      setTestingEntered(false)
    }
  }

  const handleTestStored = async () => {
    setTestingStored(true)
    try {
      const response = await fetch(`${API_BASE_URL}/api/config/email/test-stored`, {
        method: 'POST',
        headers: getAuthHeaders(),
      })
      const data = await response.json().catch(() => ({}))
      const interpreted = interpretSmtpTestResponse(response.status, data)
      if (interpreted.ok) {
        toast.success(interpreted.message)
      } else {
        toast.error(interpreted.message)
      }
    } catch (error) {
      toast.error('Error al probar la configuración guardada')
    } finally {
      setTestingStored(false)
    }
  }

  const handleSendTestEmail = async () => {
    if (sendingTest) return

    const validated = validateTestRecipientEmail(testRecipientEmail)
    if (!validated.ok) {
      toast.error(
        validated.error_code === 'RECIPIENT_REQUIRED'
          ? 'Debe indicar un correo destinatario de prueba'
          : 'El correo destinatario de prueba no es válido',
      )
      return
    }

    setSendingTest(true)
    try {
      const payload = buildSendTestPayload(validated.email)
      const response = await fetch(`${API_BASE_URL}/api/config/email/send-test`, {
        method: 'POST',
        headers: getAuthHeaders({
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify(payload),
      })
      const data = await response.json().catch(() => ({}))
      const interpreted = interpretSendTestResponse(response.status, data)
      if (interpreted.ok) {
        toast.success(interpreted.message)
      } else {
        toast.error(interpreted.message)
      }
    } catch (error) {
      toast.error('No fue posible enviar el correo de prueba.')
    } finally {
      setSendingTest(false)
    }
  }

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text)
    toast.success('Copiado al portapapeles')
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center mb-6">
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 mr-4"
        >
          <ArrowLeft className="h-5 w-5" />
          <span>Volver</span>
        </button>
        <div className="flex items-center space-x-3">
          <Mail className="h-6 w-6 text-primary-600" />
          <h1 className="text-2xl font-bold text-gray-900">Configuración de Correo SMTP</h1>
        </div>
      </div>

      <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div>
            <p className="text-sm text-gray-700">
              Configura las credenciales del servidor SMTP. La prueba de conexión autentica realmente
              con el servidor; un HTTP 200 no implica éxito si <code>success</code> es falso.
            </p>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Servidor SMTP
              </label>
              <input
                type="text"
                value={config.smtp_server}
                onChange={(e) => setConfig({ ...config, smtp_server: e.target.value })}
                className="input-field"
                placeholder="smtp.office365.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Puerto
              </label>
              <input
                type="number"
                value={config.smtp_port}
                onChange={(e) => setConfig({ ...config, smtp_port: parseInt(e.target.value) })}
                className="input-field"
                placeholder="587"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Usuario SMTP
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={config.smtp_user}
                  onChange={(e) => setConfig({ ...config, smtp_user: e.target.value })}
                  className="input-field pr-10"
                  placeholder="proyectos.td@induma.com.co"
                />
                <button
                  onClick={() => handleCopy(config.smtp_user)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <Copy className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Enviar como (Remitente visible)
              </label>
              <input
                type="email"
                value={config.from_email}
                onChange={(e) => setConfig({ ...config, from_email: e.target.value })}
                className="input-field"
                placeholder="pdfsigner.induma@induma.com.co (opcional)"
              />
              <p className="text-xs text-gray-500 mt-1">
                Si lo dejas vacío, se usará el Usuario SMTP autenticado.
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Contraseña SMTP
              </label>
              <div className="relative">
                <input
                  type="password"
                  value={config.smtp_password}
                  onChange={(e) => setConfig({ ...config, smtp_password: e.target.value })}
                  className="input-field pl-10"
                  placeholder="Tu contraseña SMTP"
                  autoComplete="new-password"
                />
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Al guardar, si deja este campo vacío se conserva la contraseña ya almacenada.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Seguridad
              </label>
              <select
                value={config.security}
                onChange={(e) => setConfig({ ...config, security: e.target.value })}
                className="input-field"
              >
                <option value="TLS">Seguro (TLS/SSL)</option>
                <option value="SSL">SSL</option>
                <option value="NONE">Sin seguridad</option>
              </select>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 mt-8 pt-6 border-t border-gray-200">
          <button
            onClick={handleSave}
            disabled={loading}
            className="flex items-center space-x-2 bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Save className="h-4 w-4" />
            <span>{loading ? 'Guardando...' : 'Guardar Configuración de Correo'}</span>
          </button>

          <button
            onClick={handleTestEntered}
            disabled={testingEntered}
            className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <TestTube className="h-4 w-4" />
            <span>{testingEntered ? 'Probando...' : 'Probar contraseña ingresada'}</span>
          </button>

          <button
            onClick={handleTestStored}
            disabled={testingStored}
            className="flex items-center space-x-2 bg-slate-700 text-white px-4 py-2 rounded-lg hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <ShieldCheck className="h-4 w-4" />
            <span>{testingStored ? 'Probando...' : 'Probar configuración guardada'}</span>
          </button>
        </div>

        <div className="mt-8 pt-6 border-t border-gray-200 space-y-4">
          <div>
            <h2 className="text-sm font-semibold text-gray-900">Correo de prueba</h2>
            <p className="text-xs text-gray-500 mt-1">
              Usa exclusivamente la configuración SMTP activa guardada. No utiliza la
              contraseña escrita en el formulario ni la modifica.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Correo destinatario de prueba
              </label>
              <input
                type="email"
                value={testRecipientEmail}
                onChange={(e) => setTestRecipientEmail(e.target.value)}
                className="input-field"
                placeholder="usuario@induma.com.co"
                autoComplete="off"
                disabled={sendingTest}
              />
            </div>
            <div>
              <button
                onClick={handleSendTestEmail}
                disabled={sendingTest}
                className="flex items-center space-x-2 bg-emerald-700 text-white px-4 py-2 rounded-lg hover:bg-emerald-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Send className="h-4 w-4" />
                <span>{sendingTest ? 'Enviando...' : 'Enviar correo de prueba'}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default EmailConfig
